import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Store, Users, Calendar, ArrowRight, Star, ChevronRight, MessageSquare, BarChart3, Award } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar.tsx';
import Footer from '../components/Footer.tsx';

const CheckIcon = ({ className }: { className?: string }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="3" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const VendorPage = () => {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  };

  const staggerChildren = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Wedify - Wedding Vendor Platform",
    "description": "Transform your wedding business with WedFlow, our all-in-one ERP + CRM platform. Streamline operations with AI-powered tools for inventory, dispatch, team management, and client communication.",
    "url": "https://wedify.com/vendors",
    "mainEntity": {
      "@type": "Product",
      "name": "Wedify",
      "description": "All-in-one ERP + CRM platform for wedding vendors",
      "offers": {
        "@type": "Offer",
        "name": "Wedding Vendor Platform",
        "description": "Professional tools and AI-powered services for wedding vendors"
      }
    }
  };

  const wedflowFeatures = [
    {
      icon: "📦",
      title: "Inventory Management",
      subtitle: "Your behind-the-scenes assistant that never sleeps.",
      description: "Think of Wedify's AI agents like digital inventory assistants — always watching stock levels, cross-checking bookings, and forecasting demand like your most organized team member. They talk to each other to prevent double-bookings, alert you when supplies run low, and keep everything ready behind the scenes so you can focus on styling the perfect setup."
    },
    {
      icon: "🚚",
      title: "Smart Dispatch",
      subtitle: "Your personal coordinator with a GPS brain.",
      description: "Imagine having a smart delivery manager who knows traffic, timelines, and your event calendar — and who never calls in sick. That's what Wedify's AI agents do here. They assign routes, sync with your inventory, and give your clients accurate delivery ETAs. It's like having a logistics expert who auto-adjusts in real time, so you never miss a beat."
    },
    {
      icon: "👥",
      title: "Team Management",
      subtitle: "Your crew chief that knows everyone's strengths.",
      description: "Think of these AI agents like a team captain who knows who's best at what, when they're available, and how much they've already done. They help assign tasks, track progress, and keep everyone aligned without you needing to manage every move. It's less time micromanaging and more time dreaming up what's next."
    },
    {
      icon: "💬",
      title: "Client Portal",
      subtitle: "Your friendly front desk that never drops the ball.",
      description: "Wedify's AI agents work here like your always-on receptionist. They send updates, remind clients about invoices or contracts, and keep all communications in one place. No more scattered messages or email threads — just a smooth, professional experience that builds trust while freeing you up for creative work."
    }
  ];

  const bottomFeatures = [
    "Offload 40% of your admin tasks to Wedify's AI assistants — like having a smart virtual team running operations.",
    "Stay in sync with design and bookings through one smart system that connects your work with couples and your team.",
    "Spend less time reacting to problems — our AI agents handle the busywork so you can focus on execution and creativity."
  ];

  useEffect(() => {
    const script = document.createElement('script');
    script.src = '//embed.typeform.com/next/embed.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-pink-50 to-purple-50">
      <Helmet>
        <title>Wedify - All-in-One Wedding Vendor Platform | Wedify</title>
        <meta name="description" content="Transform your wedding business with WedFlow. Our AI-powered platform streamlines operations, automates logistics, and enhances client experience. Join thousands of successful wedding professionals." />
        <meta name="keywords" content="wedding vendor platform, wedding business management, AI-powered wedding tools, wedding vendor software, wedding business automation" />
        <link rel="canonical" href="https://wedify.com/vendors" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <Navbar />

      <main className="pt-24 pb-20">
        <nav aria-label="Breadcrumb" className="container mx-auto px-4 mb-8">
          <ol className="flex items-center space-x-2 text-sm text-gray-600">
            <li>
              <Link to="/" className="hover:text-brand-purple">Home</Link>
            </li>
            <li>
              <span className="mx-2">/</span>
            </li>
            <li aria-current="page" className="text-brand-purple">
              Vendor Platform
            </li>
          </ol>
        </nav>

        <motion.section
          initial="initial"
          animate="animate"
          exit="exit"
          variants={staggerChildren}
        >
          {/* Hero Section */}
          <section aria-labelledby="hero-heading" className="container mx-auto px-4 mb-32">
            <div className="max-w-7xl mx-auto">
              <div className="relative rounded-3xl overflow-hidden bg-gradient-to-r from-brand-purple to-brand-purple-light p-1">
                <div className="bg-white rounded-3xl overflow-hidden">
                  <div className="grid lg:grid-cols-2 gap-0">
                    <div className="p-8 lg:p-12 flex flex-col justify-center">
                      <motion.div variants={fadeIn} className="space-y-6">
                        <span className="inline-flex items-center gap-2 text-brand-purple font-medium">
                          <Star className="h-5 w-5 fill-current" />
                          Trusted by 1000+ wedding professionals
                        </span>
                        <h1 id="hero-heading" className="text-4xl lg:text-5xl font-bold text-gray-900">
                          Transform Your Wedding Business
                        </h1>
                        <p className="text-lg text-gray-600">
                          Join the most innovative wedding platform and connect with modern couples using AI-powered matching.
                        </p>
                        <div className="flex flex-wrap gap-4">
                          <div data-tf-live="01JV5GKNJ0K4RTVH5RSXBQDQEC"></div>
                        </div>
                      </motion.div>
                    </div>
                    <div className="relative h-full min-h-[400px] lg:min-h-0">
                      <img 
                        src="https://images.pexels.com/photos/1405761/pexels-photo-1405761.jpeg"
                        alt="Professional wedding venue setup with elegant decorations"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Stats Section */}
          <motion.section variants={fadeIn} className="container mx-auto px-4 mb-32">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  { number: '50K+', label: 'Active Monthly Users', detail: 'Growing audience of engaged couples' },
                  { number: '10K+', label: 'Qualified Leads', detail: 'AI-matched potential clients' },
                  { number: '1K+', label: 'Success Stories', detail: 'Happy wedding professionals' }
                ].map((stat, index) => (
                  <motion.div
                    key={index}
                    variants={fadeIn}
                    className="glass-card p-8 text-center space-y-2"
                  >
                    <h2 className="text-5xl font-bold bg-gradient-to-r from-brand-purple to-brand-purple-light text-transparent bg-clip-text">
                      {stat.number}
                    </h2>
                    <p className="text-xl font-semibold text-gray-800">{stat.label}</p>
                    <p className="text-gray-600">{stat.detail}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.section>

          {/* Features Grid */}
          <motion.section variants={fadeIn} className="container mx-auto px-4 mb-32 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-brand-purple/5 to-transparent rounded-[3rem] blur-3xl -z-10" />
            
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-4xl font-bold mb-4 text-black">Why Choose Wedify?</h2>
                <p className="text-lg text-gray-600">Everything you need to grow your wedding business</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Main Feature Card */}
                <motion.div
                  variants={fadeIn}
                  className="lg:col-span-8 glass-card p-8 bg-gradient-to-br from-white/80 to-white/40 backdrop-blur-xl border border-white/20 rounded-3xl overflow-hidden relative group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-brand-purple/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="relative z-10">
                    <div className="flex items-start gap-6">
                      <div className="w-16 h-16 rounded-2xl bg-brand-purple/10 flex items-center justify-center">
                        <Store className="h-8 w-8 text-brand-purple" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-semibold mb-3 text-black">Smart Profile Builder</h3>
                        <p className="text-gray-600 mb-6">Create a stunning portfolio with AI-powered optimization suggestions that help you showcase your best work and attract ideal clients.</p>
                        <div className="flex flex-wrap gap-4">
                          {['AI-powered insights', 'Custom templates', 'SEO optimization'].map((feature, index) => (
                            <span key={index} className="px-4 py-2 rounded-full bg-brand-purple/5 text-brand-purple text-sm">
                              {feature}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Analytics Card */}
                <motion.div
                  variants={fadeIn}
                  className="lg:col-span-4 glass-card p-8 bg-gradient-to-br from-white/80 to-white/40 backdrop-blur-xl border border-white/20 rounded-3xl group hover:bg-white/50 transition-colors"
                >
                  <div className="flex flex-col h-full">
                    <div className="w-12 h-12 rounded-2xl bg-brand-purple/10 flex items-center justify-center mb-6">
                      <BarChart3 className="h-6 w-6 text-brand-purple" />
                    </div>
                    <h3 className="text-xl font-semibold mb-3 text-black">Analytics Dashboard</h3>
                    <p className="text-gray-600 mb-6">Track performance metrics and gain valuable insights into client interactions.</p>
                    <div className="mt-auto">
                      <div className="flex items-center gap-2 text-brand-purple">
                        <span className="text-sm font-medium">Learn more</span>
                        <ChevronRight className="h-4 w-4" />
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Communication Hub */}
                <motion.div
                  variants={fadeIn}
                  className="lg:col-span-4 glass-card p-8 bg-gradient-to-br from-white/80 to-white/40 backdrop-blur-xl border border-white/20 rounded-3xl group hover:bg-white/50 transition-colors"
                >
                  <div className="flex flex-col h-full">
                    <div className="w-12 h-12 rounded-2xl bg-brand-purple/10 flex items-center justify-center mb-6">
                      <MessageSquare className="h-6 w-6 text-brand-purple" />
                    </div>
                    <h3 className="text-xl font-semibold mb-3 text-black">Client Communication</h3>
                    <p className="text-gray-600 mb-6">Streamlined messaging and notification system to keep you connected.</p>
                    <div className="mt-auto">
                      <div className="flex items-center gap-2 text-brand-purple">
                        <span className="text-sm font-medium">Learn more</span>
                        <ChevronRight className="h-4 w-4" />
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Booking System */}
                <motion.div
                  variants={fadeIn}
                  className="lg:col-span-8 glass-card p-8 bg-gradient-to-br from-white/80 to-white/40 backdrop-blur-xl border border-white/20 rounded-3xl group hover:bg-white/50 transition-colors"
                >
                  <div className="flex items-start gap-6">
                    <div className="w-12 h-12 rounded-2xl bg-brand-purple/10 flex items-center justify-center">
                      <Calendar className="h-6 w-6 text-brand-purple" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-3 text-black">Smart Booking System</h3>
                      <p className="text-gray-600 mb-4">Automated scheduling and payment processing to streamline your booking workflow.</p>
                      <div className="flex flex-wrap gap-3">
                        {['Calendar sync', 'Automated reminders', 'Payment processing'].map((feature, index) => (
                          <span key={index} className="px-3 py-1 rounded-full bg-brand-purple/5 text-brand-purple text-sm">
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Reviews & Recognition */}
                <motion.div
                  variants={fadeIn}
                  className="lg:col-span-12 glass-card p-8 bg-gradient-to-br from-white/80 to-white/40 backdrop-blur-xl border border-white/20 rounded-3xl"
                >
                  <div className="grid md:grid-cols-2 gap-8 items-center">
                    <div>
                      <div className="w-12 h-12 rounded-2xl bg-brand-purple/10 flex items-center justify-center mb-6">
                        <Award className="h-6 w-6 text-brand-purple" />
                      </div>
                      <h3 className="text-xl font-semibold mb-3 text-black">Reviews & Recognition</h3>
                      <p className="text-gray-600">Build your reputation with verified reviews and showcase your achievements.</p>
                    </div>
                    <div className="flex gap-4 overflow-x-auto pb-4 md:justify-end">
                      {[...Array(3)].map((_, index) => (
                        <div key={index} className="flex-none w-32 h-32 rounded-2xl bg-brand-purple/5 flex items-center justify-center">
                          <Star className="h-8 w-8 text-brand-purple" />
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.section>

          {/* How It Works Section */}
          <motion.section variants={fadeIn} className="container mx-auto px-4 mb-32">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-3xl lg:text-4xl font-bold text-center mb-16 text-black">How It Works</h2>
              
              {/* Create Storefront */}
              <div className="grid lg:grid-cols-2 gap-16 items-center mb-32">
                <div className="order-2 lg:order-1">
                  <h3 className="text-2xl lg:text-3xl font-bold text-brand-purple mb-6">
                    Create your storefront
                  </h3>
                  <p className="text-gray-600 mb-8">
                    Build your personalized storefront that captures attention in our innovative swipe-based discovery platform. Our AI-powered system analyzes couple interactions to provide you with engagement insights, helping you optimize your profile for maximum visibility and better leads.
                  </p>
                  <ul className="space-y-4">
                    {[
                      'Showcase your work in our immersive swipe experience',
                      'Get AI-powered insights on profile engagement',
                      'Optimize your storefront with data-driven recommendations'
                    ].map((item, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-brand-purple"></div>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="order-1 lg:order-2">
                  <img 
                    src="/UpdatedMobile1.png" 
                    alt="Storefront creation" 
                    className="rounded-2xl shadow-2xl aspect-[16/9] object-cover w-full"
                  />
                </div>
              </div>

              {/* Get Matched */}
              <div className="grid lg:grid-cols-2 gap-16 items-center mb-32">
                <div>
                  <img 
                    src="/UpdatedMobile.png" 
                    alt="Couple matching" 
                    className="rounded-2xl shadow-2xl aspect-[4/3] object-cover w-full"
                  />
                </div>
                <div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-brand-purple mb-6">
                    Get matched with ideal couples
                  </h3>
                  <p className="text-gray-600 mb-8">
                    Our intelligent platform showcases your business across multiple dynamic layouts - from immersive swipe experiences to curated grid displays. Our AI analyzes couple interactions and preferences in real-time, automatically optimizing your visibility in the formats that drive the most engagement for your specific services.
                  </p>
                  <ul className="space-y-4">
                    {[
                      'Smart placement across multiple discovery formats',
                      'AI-optimized visibility based on couple preferences',
                      'Real-time performance insights and layout optimization'
                    ].map((item, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-brand-purple"></div>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Grow Your Business */}
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                <div className="order-2 lg:order-1">
                  <h3 className="text-2xl lg:text-3xl font-bold text-brand-purple mb-6">
                    Grow your business
                  </h3>
                  <p className="text-gray-600 mb-8">
                    Get featured as a Spotlight Vendor and showcase your business in its best light. Our platform highlights your unique services, ratings, and happy couple reviews, while our AI-powered system ensures your profile reaches couples who value your expertise.
                  </p>
                  <ul className="space-y-4">
                    {[
                      'Premium spotlight placement with detailed service showcase',
                      'Highlight your ratings and happy couple testimonials',
                      'Showcase your complete service packages and expertise'
                    ].map((item, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-brand-purple"></div>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="order-1 lg:order-2">
                  <img 
                    src="https://images.pexels.com/photos/3014856/pexels-photo-3014856.jpeg" 
                    alt="Business growth" 
                    className="rounded-2xl shadow-2xl aspect-[4/3] object-cover w-full"
                  />
                </div>
              </div>
            </div>
          </motion.section>

          {/* WedFlow Section */}
          <motion.section variants={fadeIn} className="container mx-auto px-4 mb-32">
            <div className="max-w-7xl mx-auto">
              <div className="bg-[#1E1E2E] rounded-3xl p-12 md:p-16 overflow-hidden relative">
                {/* Background gradient effects */}
                <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-brand-purple/20 blur-[120px] rounded-full"></div>
                <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-pink-500/20 blur-[120px] rounded-full"></div>
                
                <div className="text-center mb-16 relative z-10">
                  <h2 className="text-4xl md:text-5xl font-bold text-pink-400 mb-6">
                    Coming Soon: WedFlow
                  </h2>
                  <p className="text-lg text-gray-300 max-w-3xl mx-auto">
                    Your all-in-one ERP + CRM platform built exclusively for wedding vendors. Streamline your operations, automate logistics, and wow your clients with ease.
                  </p>
                  <div className="mt-10" data-tf-live="01JV5JJJ8JM7QN0H5QJB8XSMG3"></div>
                  <p className="text-sm text-gray-400 mt-4">
                    Early access includes onboarding + feature input
                  </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-6">
                  {wedflowFeatures.map((feature, index) => (
                    <motion.div
                      key={index}
                      variants={fadeIn}
                      className="bg-[#2A2A3C]/50 backdrop-blur-xl rounded-2xl p-8 border border-white/5 hover:border-pink-500/20 transition-all duration-500 group relative overflow-hidden"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-pink-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                      
                      <div className="relative z-10">
                        <div className="w-12 h-12 rounded-full bg-pink-500/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                          <span className="text-2xl">{feature.icon}</span>
                        </div>
                        <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                        <p className="text-pink-400 mb-4 text-sm">{feature.subtitle}</p>
                        <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Bottom features */}
                <div className="grid md:grid-cols-3 gap-6 mt-8">
                  {bottomFeatures.map((text, index) => (
                    <div key={index} className="flex items-center gap-3 bg-[#2A2A3C]/30 rounded-xl p-4 border border-white/5">
                      <div className="w-5 h-5 rounded-full bg-pink-500/20 flex items-center justify-center flex-shrink-0">
                        <CheckIcon className="w-3 h-3 text-pink-500" />
                      </div>
                      <p className="text-sm text-gray-300">{text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.section>

          {/* CTA Section */}
          <motion.section variants={fadeIn} className="container mx-auto px-4">
            <div className="max-w-7xl mx-auto">
              <div className="rounded-3xl bg-gradient-to-r from-brand-purple to-brand-purple-light p-12 md:p-16 text-center text-white">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to grow your business?</h2>
                <p className="text-lg mb-8 text-white/90">Join thousands of successful wedding professionals on Wedify</p>
                <div data-tf-live="01JV5G3VRCNSKGBAAM3B6SGJG6"></div>
              </div>
            </div>
          </motion.section>
        </motion.section>
      </main>

      <Footer />
    </div>
  );
};

export default VendorPage;